// ═══════════════════════════════════════════════════════════════════════════════
// Receipt edit — helper: update content when pending or employer asked for changes
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  formatReceiptMoney,
  getTodayDate,
  isReceiptTotalMismatch,
  parseReceiptTotalAmount,
  RECEIPT_STATUS,
  sumReceiptLineItemAmounts,
  type Receipt,
  type ReceiptItem,
} from '@fdh/shared';
import { ReceiptImageLightbox } from '../../components/receipt/ReceiptImageLightbox';
import { ReceiptTotalMismatchBanner } from '../../components/receipt/ReceiptTotalMismatchBanner';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { AppButton } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { ApiClientError, receiptApi } from '../../lib/api';

type EditFormState = {
  receiptDate: string;
  company: string;
  shop: string;
  currency: string;
  totalAmount: number | '';
  items: ReceiptItem[];
  remark: string;
  version: number;
};

const formControlClass =
  'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const itemShellClass =
  'grid grid-cols-12 gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-800/90';

const itemFieldClass =
  'rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const TrashIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2">
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

const receiptToForm = (r: Receipt): EditFormState => ({
  receiptDate: r.receiptDate,
  company: r.company,
  shop: r.shop,
  currency: r.currency?.trim() !== '' ? r.currency.trim() : 'HKD',
  totalAmount: r.totalAmount,
  items:
    r.items.length > 0
      ? r.items.map((it) => ({ ...it }))
      : [{ item: '', qty: '', amount: '' }],
  remark: r.remark,
  version: r.version,
});

const resolveEditFormTotal = (form: EditFormState): number | null => {
  const total = parseReceiptTotalAmount(form.totalAmount);
  if (total === null || total < 0) {
    return null;
  }
  return total;
};

const getEditFormSubmitError = (form: EditFormState, t: (key: string) => string): string | null => {
  if (!form.receiptDate || !ISO_DATE_RE.test(form.receiptDate)) {
    return t('receipt.analyze.dateRequired');
  }
  if (form.company.trim() === '') {
    return t('receipt.analyze.companyRequired');
  }
  if (resolveEditFormTotal(form) === null) {
    return t('receipt.analyze.invalidTotal');
  }
  return null;
};

const editFormHasAmountMismatch = (form: EditFormState): boolean => {
  if (resolveEditFormTotal(form) === null) {
    return false;
  }
  return isReceiptTotalMismatch(form.totalAmount, form.items);
};

export const ReceiptEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [form, setForm] = useState<EditFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<'status' | 'notOwner' | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mismatchConfirmOpen, setMismatchConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) {
      setLoadError(t('receipt.detail.loadError'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setBlocked(null);
    try {
      const res = await receiptApi.get(id);
      if (!res.success || !res.data) {
        setLoadError(res.error ?? t('receipt.detail.loadError'));
        setReceipt(null);
        setForm(null);
        return;
      }
      const r = res.data;
      setReceipt(r);
      setForm(receiptToForm(r));
      if (r.status !== RECEIPT_STATUS.SAVE) {
        setBlocked('status');
        return;
      }
      if (user?.id && r.submittedByUserId !== user.id) {
        setBlocked('notOwner');
      }
    } catch {
      setLoadError(t('receipt.detail.loadError'));
      setReceipt(null);
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [id, t, user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePatch = (patch: Partial<EditFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
    setSaveError(null);
  };

  const handleItemPatch = (itemIndex: number, patch: Partial<ReceiptItem>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const nextItems = prev.items.map((it, i) => (i === itemIndex ? { ...it, ...patch } : it));
      return { ...prev, items: nextItems };
    });
    setSaveError(null);
  };

  const handleAddItem = () => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: [...prev.items, { item: '', qty: '', amount: '' }],
      };
    });
    setSaveError(null);
  };

  const handleRemoveItem = (itemIndex: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== itemIndex),
      };
    });
    setSaveError(null);
  };

  const detailHref = id ? `/receipts/${encodeURIComponent(id)}` : '/receipts';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(detailHref, { replace: true });
  };

  const handleSave = async () => {
    if (!id || !form) return;
    const submitError = getEditFormSubmitError(form, t);
    if (submitError) {
      setSaveError(submitError);
      return;
    }
    const total = resolveEditFormTotal(form);
    if (total === null) {
      setSaveError(t('receipt.analyze.invalidTotal'));
      return;
    }
    const cleanedItems = form.items
      .filter((it) => it.item.trim() !== '')
      .map((it) => ({
        item: it.item.trim(),
        qty: it.qty,
        amount: it.amount,
      }));

    setSaving(true);
    setSaveError(null);
    try {
      const res = await receiptApi.update(id, {
        receiptDate: form.receiptDate,
        company: form.company.trim(),
        shop: form.shop.trim(),
        currency: form.currency.trim().toUpperCase() || 'HKD',
        totalAmount: total,
        items: cleanedItems,
        remark: form.remark.trim(),
        version: form.version,
      });
      if (res.success && res.data) {
        showSuccessToast(t('toast.receipt.editSuccess'));
        navigate(`/receipts/${encodeURIComponent(id)}`, { replace: true });
        return;
      }
      setSaveError(typeof res.error === 'string' && res.error.trim() !== '' ? res.error : t('receipt.edit.saveError'));
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setSaveError(t('task.review.versionConflict'));
        await load();
        return;
      }
      setSaveError(resolveApiErrorMessage(err, t, 'receipt.edit.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRequestSave = () => {
    if (!form) {
      return;
    }
    const submitError = getEditFormSubmitError(form, t);
    if (submitError) {
      setSaveError(submitError);
      return;
    }
    if (editFormHasAmountMismatch(form)) {
      setMismatchConfirmOpen(true);
      return;
    }
    void handleSave();
  };

  const handleConfirmMismatchSave = () => {
    setMismatchConfirmOpen(false);
    void handleSave();
  };

  const handleCancelMismatchConfirm = () => {
    setMismatchConfirmOpen(false);
  };

  const formResolvedTotal = form ? resolveEditFormTotal(form) : null;
  const formLineSum = form ? sumReceiptLineItemAmounts(form.items) : 0;
  const showAmountMismatchBanner =
    form !== null && formResolvedTotal !== null && editFormHasAmountMismatch(form);
  const mismatchConfirmDescription =
    form && formResolvedTotal !== null
      ? t('receipt.analyze.totalMismatchConfirmSingle', {
          total: formatReceiptMoney(formResolvedTotal),
          lineSum: formatReceiptMoney(formLineSum),
        })
      : '';

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('task.action.back')}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('receipt.edit.title')}</h1>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('receipt.edit.subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-4 py-6 pb-28">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {loadError}
          </p>
        ) : blocked === 'status' ? (
          <p className="text-sm text-amber-800 dark:text-amber-200" role="status">
            {t('receipt.edit.blockedStatus')}
          </p>
        ) : blocked === 'notOwner' ? (
          <p className="text-sm text-amber-800 dark:text-amber-200" role="status">
            {t('receipt.edit.blockedNotOwner')}
          </p>
        ) : receipt && form ? (
          <div className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            {receipt.originalImageUrl && receipt.originalImageUrl.trim() !== '' && id ? (
              <div>
                <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t('receipt.detail.photoHeading')}
                </h2>
                <ReceiptImageLightbox
                  receiptId={id}
                  alt={t('receipt.detail.photoAlt')}
                  enlargeAriaLabel={t('receipt.analyze.enlargeImageAria')}
                  closeLabel={t('common.closeViewer')}
                />
              </div>
            ) : null}

            <div className="grid gap-3">
              <IsoDatePicker
                id="receipt-edit-date"
                label={t('receipt.analyze.dateLabel')}
                value={form.receiptDate}
                onChange={(next) => handlePatch({ receiptDate: next })}
                maxDate={getTodayDate()}
                captionLayout="dropdown"
              />

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('receipt.analyze.field.company')}
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => handlePatch({ company: e.target.value })}
                  className={formControlClass}
                  autoComplete="organization"
                />
              </label>

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('receipt.analyze.field.total')}
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.totalAmount === '' ? '' : String(form.totalAmount)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      handlePatch({ totalAmount: '' });
                      return;
                    }
                    const n = parseFloat(v);
                    handlePatch({
                      totalAmount: Number.isFinite(n) ? n : '',
                    });
                  }}
                  className={formControlClass}
                />
                {showAmountMismatchBanner && formResolvedTotal !== null ? (
                  <ReceiptTotalMismatchBanner
                    message={t('receipt.analyze.totalMismatchBanner', {
                      total: formatReceiptMoney(formResolvedTotal),
                      lineSum: formatReceiptMoney(formLineSum),
                    })}
                  />
                ) : null}
              </label>

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('receipt.analyze.field.remark')}
                <textarea
                  value={form.remark}
                  onChange={(e) => handlePatch({ remark: e.target.value })}
                  rows={2}
                  className={formControlClass}
                />
              </label>

              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{t('receipt.analyze.items')}</p>
                <div className="mt-2 grid grid-cols-12 gap-2 rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  <p className="col-span-12 sm:col-span-5">{t('receipt.analyze.itemName')}</p>
                  <p className="col-span-4 sm:col-span-3">{t('receipt.analyze.qty')}</p>
                  <p className="col-span-6 sm:col-span-3">{t('receipt.analyze.amount')}</p>
                  <p className="col-span-2 text-right sm:col-span-1">{t('receipt.analyze.itemsAction')}</p>
                </div>
                <ul className="mt-2 space-y-2">
                  {form.items.map((it, idx) => (
                    <li key={`receipt-edit-item-${idx}`} className={itemShellClass}>
                      <input
                        type="text"
                        aria-label={t('receipt.analyze.itemName')}
                        value={it.item}
                        onChange={(e) => handleItemPatch(idx, { item: e.target.value })}
                        className={`col-span-12 sm:col-span-5 ${itemFieldClass}`}
                      />
                      <input
                        type="text"
                        aria-label={t('receipt.analyze.qty')}
                        value={it.qty === '' ? '' : String(it.qty)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            handleItemPatch(idx, { qty: '' });
                            return;
                          }
                          const n = Number(raw);
                          handleItemPatch(idx, {
                            qty: Number.isFinite(n) && raw === String(n) ? n : raw,
                          });
                        }}
                        className={`col-span-4 sm:col-span-3 ${itemFieldClass}`}
                      />
                      <input
                        type="number"
                        aria-label={t('receipt.analyze.amount')}
                        value={it.amount === '' ? '' : it.amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') {
                            handleItemPatch(idx, { amount: '' });
                            return;
                          }
                          const n = parseFloat(v);
                          handleItemPatch(idx, {
                            amount: Number.isFinite(n) ? n : '',
                          });
                        }}
                        className={`col-span-6 sm:col-span-3 ${itemFieldClass}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        aria-label={t('receipt.analyze.removeItem')}
                        title={t('receipt.analyze.removeItem')}
                        className="col-span-2 inline-flex items-center justify-center rounded border border-red-200 text-red-700 sm:col-span-1 dark:border-red-800 dark:text-red-300"
                      >
                        <TrashIcon />
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={handleAddItem}
                  aria-label={t('receipt.analyze.addItem')}
                  className="mt-2 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary-300 bg-primary-50/90 px-4 py-2.5 text-sm font-semibold text-primary-800 shadow-sm transition-colors hover:border-primary-400 hover:bg-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-primary-600/60 dark:bg-primary-950/50 dark:text-primary-200 dark:hover:bg-primary-900/60 sm:w-auto"
                >
                  <span className="text-base font-bold leading-none" aria-hidden>
                    +
                  </span>
                  <span>{t('receipt.analyze.addItem')}</span>
                </button>
              </div>
            </div>

            {saveError ? (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {saveError}
              </p>
            ) : null}

            <AppButton
              type="button"
              variant="primary"
              fullWidth
              onClick={handleRequestSave}
              disabled={saving}
              aria-busy={saving}
              className="shadow-sm"
            >
              {saving ? t('receipt.edit.saving') : t('receipt.edit.save')}
            </AppButton>
          </div>
        ) : null}
      </main>

      <ConfirmDialog
        open={mismatchConfirmOpen}
        title={t('receipt.analyze.totalMismatchConfirmTitle')}
        description={mismatchConfirmDescription}
        confirmLabel={t('receipt.analyze.totalMismatchConfirmConfirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmMismatchSave}
        onCancel={handleCancelMismatchConfirm}
        confirmDisabled={saving}
        cancelDisabled={saving}
      />
    </div>
  );
};
